"use client"

import { Link } from 'react-router-dom'
import { ArrowLeft, Video } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextImageToVideoGallery } from '@/components/gallery/TextImageToVideoGallery'
import { VideoGalleryItem } from '@/models/gallery'
// Import the StyleX styles
import { videoPageStyles } from '@/styles/VideoPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Runway Video',
    shortName: 'Runway',
    modelKey: 'runway-video',
    creditsPerVideo: 25,
    heroBgVideo: 'https://cdn.aibrush.co/docs/videos/models/runway-video/hero-bg.mp4',
    heroBgPoster: 'https://cdn.aibrush.co/docs/videos/models/runway-video/hero-bg.png',
    providerUrl: 'https://runwayml.com/research/introducing-runway-gen-4/',
    providerName: 'Runway ML',
    pageUrl: 'https://docs.aibrush.co/videos-models/runway-video',
} as const;

const textToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "runway-video-1",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-1.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-1.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-1.png"],
        className: "",
        prompt: "A short, dramatic, and mystical video. A female warrior in black robes stands powerfully in an ancient, ethereal hall. Her long, flowing red cape billows dramatically behind her as if caught in an unseen wind, revealing glimpses of intricate carvings on the surrounding stone pillars. She draws her sword with a fluid, graceful motion, performing intricate and powerful movements with the blade, slicing through the air with precision and elegance. The atmosphere is filled with subtle, swirling dust motes illuminated by faint, ethereal light rays that pierce through the shadows. A slow, cinematic zoom-in enhances the intensity, focusing on her determined expression and the dynamic dance of her sword, which reflects a cold, metallic gleam as the light shifts.",
    },
    {
        id: "runway-video-6",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-6.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-6.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-6.png"],
        className: "",
        prompt: "A short, profoundly emotional video focusing on the heartbroken, big-eyed creature from the image. The camera slowly zooms in on its face, then pans around the messy, cardboard-strewn environment, highlighting the overwhelming sense of loneliness. Subtle animation shows the creature looking around with a deeply sorrowful and wistful expression, a torrent of tears streaming down its face, its small body wracked with deep, heartbreaking sobs. As the video ends, a single ray of warm light gently illuminates the creature, offering a glimmer of tender hope amidst its profound sadness. The mood is melancholic and sorrowful, evolving towards a fragile sense of hope. Audio includes the soft, ambient sounds of distant city life, quiet wind, the sound of heartbroken whimpers, and a gentle, melancholic piano melody that swells slightly with the arrival of the light."
    },
    {
        id: "runway-video-2",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-2.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-2.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-2.png"],
        className: "",
        prompt: "A cool cat wearing McDonald's clothes and sunglasses is walking confidently through a vibrant, bustling city street. The cat stops, takes off its sunglasses with a paw, winks at the camera, and then continues walking. The city has neon lights and futuristic elements. Animated, cinematic, cool, and smooth motion."
    },
    {
        id: "runway-video-3",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-3.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-3.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-3.png"],
        className: "",
        prompt: "A heartwarming and charming animated video. Two cute guinea pigs, one white and one black and brown, are roasting marshmallows over a campfire at night. The fire flickers warmly, casting gentle light on their faces. The guinea pigs are sitting in miniature wooden chairs, holding sticks with marshmallows. The background features dark trees, a crescent moon, and twinkling stars. The mood is cozy, peaceful, and joyful."
    },
    {
        id: "runway-video-4",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-4.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-4.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-4.png"],
        className: "",
        prompt: "The woman's white crocheted cloak gently flows in the wind as the horse subtly moves forward, its hooves creating soft dust trails. Fine dust or sand is gently blowing across the desert floor. The camera slowly tracks alongside the horse and rider, subtly panning to reveal the expansive desert landscape. The horse occasionally shifts its head, and the woman's gaze calmly surveys the surroundings."
    },
    {
        id: "runway-video-5",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/sample-5.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-5.png",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-5.png"],
        className: "",
        prompt: "A time-lapse sequence showing the transformation of a desert landscape from dawn to dusk. The camera remains stationary as the sun moves across the sky, casting dramatic shadows and changing the colors of the sand from cool blues to warm oranges. The scene captures the passage of time with cinematic beauty."
    }
];

const allTextImageToVideoImages: string[] = [
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-1.png",
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-6.png",
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-2.png",
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-3.png",
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-4.png",
    "https://cdn.aibrush.co/docs/videos/models/runway-video/thumbnail-5.png",
];

export default function RunwayVideoPage() {
    return (
        <div {...stylex.props(videoPageStyles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(videoPageStyles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(videoPageStyles.heroSection)}>
                <div {...stylex.props(videoPageStyles.heroBackground)}>
                    <video
                        src={MODEL_CONFIG.heroBgVideo}
                        poster={MODEL_CONFIG.heroBgPoster}
                        {...stylex.props(videoPageStyles.heroBgVideo)}
                        autoPlay
                        muted
                        playsInline
                    />
                    <div {...stylex.props(videoPageStyles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(videoPageStyles.heroContent)}>
                    <h2 {...stylex.props(videoPageStyles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(videoPageStyles.heroDescription)}>
                        Experience professional-grade AI video generation with <strong>Text & Image to Video</strong> capabilities.
                        Create stunning, cinematic videos with unmatched quality and creative control.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(videoPageStyles.heroStats)}>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Video size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Text & Image to Video</span>
                        </div>
                    </div>
                    <div {...stylex.props(videoPageStyles.creditCostBadge)}>{MODEL_CONFIG.creditsPerVideo} credits per video</div>
                </div>
            </section>

            {/* Text & Image to Video Gallery */}
            <TextImageToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.name}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video.`}
                title="Text & Image to Video Gallery"
                description={`Experience advanced video generation with image references using <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.providerName}</a>. Transform static images into dynamic videos with precise control. <strong>Click on any video</strong> to see how it was created.`}
                items={textToVideoGalleryItems}
                allImages={allTextImageToVideoImages}
            />
        </div>
    )
}